import React, { useState, useEffect, useCallback } from 'react';
import ComplianceBadges from '../components/ComplianceBadges';

const API_BASE = process.env.REACT_APP_API_URL || '';

/* ── Protocol visit definitions ── */
const PROTOCOL_VISITS: Record<string, string[]> = {
  'sym004-09': [
    'Screening', 'C1/D1', 'C1/D8', 'C1/D15', 'C1/D22',
    'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15', 'EOT', '1M-FUP',
  ],
  'herald': [
    'V1 Day 1', 'V2 Day 29', 'V3 Day 57', 'V4 Day 85',
    'V5 Day 113', 'V6 Day 141', 'V7 Day 169', 'EOT FUP',
  ],
};

const PROTOCOL_LABELS: Record<string, string> = {
  'sym004-09': 'Sym004-09',
  'herald': 'HERALD',
};

/* ── Types ── */
interface FormField {
  procedure: string;
  field_name: string;
  field_type: string;
  required: boolean;
  default_value: string;
  options: string[];
  metadata: Record<string, string>;
}

interface GeneratedForm {
  form_id: string;
  protocol_id: string;
  visit_name: string;
  form_type: string;
  patient_id: string;
  fields: FormField[];
}

interface FormEntry {
  procedure: string;
  field_name: string;
  value: string;
}

interface Submission {
  form_id: string;
  patient_id: string;
  visit_name: string;
  form_type: string;
  submitted_by: string;
  timestamp: string;
}

/* ── Shared styles ── */
const S = {
  page: {
    backgroundColor: '#0d1117', minHeight: '100vh', color: '#e2e8f0',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    padding: '24px 32px',
  } as React.CSSProperties,
  card: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8,
    padding: '16px 20px', marginBottom: 20,
  } as React.CSSProperties,
  heading: { margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' } as React.CSSProperties,
  subheading: { margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#e2e8f0' } as React.CSSProperties,
  label: { fontSize: 13, color: '#a0aec0', marginBottom: 4, display: 'block' } as React.CSSProperties,
  select: {
    backgroundColor: '#2d3748', color: '#e2e8f0', border: '1px solid #4a5568',
    borderRadius: 6, padding: '8px 12px', fontSize: 14, width: '100%', outline: 'none',
  } as React.CSSProperties,
  input: {
    backgroundColor: '#2d3748', color: '#e2e8f0', border: '1px solid #4a5568',
    borderRadius: 6, padding: '8px 12px', fontSize: 14, width: '100%', outline: 'none',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  row: { display: 'flex', gap: 16, flexWrap: 'wrap' as const, alignItems: 'flex-end' },
  col: { flex: 1, minWidth: 160 },
  th: {
    textAlign: 'left' as const, padding: '10px 12px', fontSize: 12, fontWeight: 600,
    color: '#a0aec0', textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    borderBottom: '1px solid #2d3748',
  } as React.CSSProperties,
  td: {
    padding: '8px 12px', fontSize: 14, color: '#e2e8f0', borderBottom: '1px solid #2d3748',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  cellInput: {
    backgroundColor: '#2d3748', color: '#e2e8f0', border: '1px solid #4a5568',
    borderRadius: 4, padding: '6px 8px', fontSize: 13, width: '100%', outline: 'none',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
};

function btnStyle(color: string, active = true): React.CSSProperties {
  return {
    backgroundColor: active ? color : 'transparent',
    color: active ? '#fff' : '#a0aec0',
    border: active ? 'none' : '1px solid #4a5568',
    borderRadius: 6, padding: '10px 20px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  };
}

function nowISO(): string {
  return new Date().toISOString().slice(0, 16);
}

/* ── Component ── */
export default function FormGenerator() {
  const [protocolId, setProtocolId] = useState('sym004-09');
  const [visitName, setVisitName] = useState(PROTOCOL_VISITS['sym004-09'][0]);
  const [patientId, setPatientId] = useState('PAT-001');
  const [formType, setFormType] = useState<'nurse' | 'pi'>('nurse');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<GeneratedForm | null>(null);
  const [entries, setEntries] = useState<Record<string, FormEntry>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  /* Reset visit when protocol changes */
  useEffect(() => {
    setVisitName(PROTOCOL_VISITS[protocolId][0]);
    setForm(null);
    setEntries({});
  }, [protocolId]);

  /* Fetch submissions */
  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/forms/submissions?protocol_id=${protocolId}&patient_id=${patientId}`);
      if (res.ok) setSubmissions(await res.json());
    } catch { /* silent */ }
  }, [protocolId, patientId]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  /* Show toast then auto-hide */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  /* Generate form */
  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setForm(null);
    setEntries({});
    try {
      const res = await fetch(`${API_BASE}/api/v1/forms/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ protocol_id: protocolId, visit_name: visitName, form_type: formType, patient_id: patientId }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data: GeneratedForm = await res.json();
      setForm(data);
      /* Seed entries from fields */
      const init: Record<string, FormEntry> = {};
      data.fields.forEach((f, i) => {
        const key = `${i}-${f.procedure}-${f.field_name}`;
        init[key] = {
          procedure: f.procedure,
          field_name: f.field_name,
          value: f.default_value || (f.field_name === 'timestamp' ? nowISO() : ''),
        };
      });
      setEntries(init);
    } catch (err: any) {
      setToast('Failed to generate form');
    } finally {
      setLoading(false);
    }
  }, [protocolId, visitName, formType, patientId]);

  /* Update a single entry */
  const updateEntry = useCallback((key: string, value: string) => {
    setEntries(prev => ({ ...prev, [key]: { ...prev[key], value } }));
  }, []);

  /* Submit form */
  const handleSubmit = useCallback(async () => {
    if (!form) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_id: form.form_id,
          patient_id: patientId,
          visit_name: visitName,
          protocol_id: protocolId,
          form_type: formType,
          entries: Object.values(entries),
          submitted_by: formType === 'nurse' ? 'Nurse-01' : 'PI-Dr-Smith',
          timestamp: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setToast('Form submitted successfully');
      fetchSubmissions();
    } catch {
      setToast('Submission failed');
    } finally {
      setSubmitting(false);
    }
  }, [form, entries, patientId, visitName, protocolId, formType, fetchSubmissions]);

  /* ── Grouped fields by procedure for table rows ── */
  const groupedRows = form ? groupFieldsByProcedure(form.fields) : [];

  return (
    <div style={S.page}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 999,
          backgroundColor: toast.includes('success') ? '#48bb78' : '#f56565',
          color: '#fff', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={S.heading}>Protocol Form Generator</h1>
        <ComplianceBadges badges={['21 CFR Part 11', 'ICH E6(R3)', 'ALCOA']} />
      </div>

      {/* Selectors */}
      <div style={S.card}>
        <div style={{ ...S.row, marginBottom: 16 }}>
          <div style={S.col}>
            <label style={S.label}>Protocol</label>
            <select style={S.select} value={protocolId} onChange={e => setProtocolId(e.target.value)}>
              {Object.entries(PROTOCOL_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div style={S.col}>
            <label style={S.label}>Visit</label>
            <select style={S.select} value={visitName} onChange={e => setVisitName(e.target.value)}>
              {PROTOCOL_VISITS[protocolId].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div style={S.col}>
            <label style={S.label}>Patient ID</label>
            <input style={S.input} value={patientId} onChange={e => setPatientId(e.target.value)} />
          </div>
        </div>

        {/* Form type toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['nurse', 'pi'] as const).map(t => (
            <button key={t} style={btnStyle('#4299e1', formType === t)}
              onClick={() => { setFormType(t); setForm(null); setEntries({}); }}>
              {t === 'nurse' ? 'Nurse Form' : 'PI Form'}
            </button>
          ))}
        </div>

        {/* Generate */}
        <button style={{ ...btnStyle('#63b3ed'), opacity: loading ? 0.6 : 1 }}
          onClick={handleGenerate} disabled={loading}>
          {loading ? 'Generating form...' : 'Generate Form'}
        </button>
      </div>

      {/* Form display */}
      {form && (
        <div style={S.card}>
          <h3 style={S.subheading}>
            {form.form_type === 'nurse' ? 'Nurse Collection Form' : 'PI Assessment Form'}
            <span style={{ fontSize: 13, color: '#718096', fontWeight: 400, marginLeft: 12 }}>
              {form.form_id} | {form.visit_name} | {form.patient_id}
            </span>
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {formType === 'nurse' ? (
                  <tr>
                    {['Procedure', 'Sample Type', 'Volume (mL)', 'Drawn By', 'Timestamp', 'Notes'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                ) : (
                  <tr>
                    {['Procedure', 'Finding', 'Details', 'Signed By', 'Timestamp'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {groupedRows.map((row, ri) => {
                  const keyBase = (fname: string) => {
                    const idx = form.fields.findIndex(
                      f => f.procedure === row.procedure && f.field_name === fname
                    );
                    return `${idx}-${row.procedure}-${fname}`;
                  };
                  const val = (fname: string) => entries[keyBase(fname)]?.value || '';
                  const set = (fname: string, v: string) => updateEntry(keyBase(fname), v);
                  const meta = row.metadata || {};

                  if (formType === 'nurse') {
                    return (
                      <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'transparent' : 'rgba(45,55,72,0.3)' }}>
                        <td style={S.td}>{row.procedure}</td>
                        <td style={S.td}>
                          <input style={S.cellInput} value={val('sample_type') || meta.sample_type || ''}
                            onChange={e => set('sample_type', e.target.value)} placeholder="e.g. EDTA" />
                        </td>
                        <td style={{ ...S.td, width: 90 }}>
                          <input style={S.cellInput} type="number" value={val('volume') || meta.volume || ''}
                            onChange={e => set('volume', e.target.value)} placeholder="mL" />
                        </td>
                        <td style={S.td}>
                          <input style={S.cellInput} value={val('drawn_by')}
                            onChange={e => set('drawn_by', e.target.value)} placeholder="Name" />
                        </td>
                        <td style={S.td}>
                          <input style={{ ...S.cellInput, minWidth: 170 }} type="datetime-local"
                            value={val('timestamp') || nowISO()}
                            onChange={e => set('timestamp', e.target.value)} />
                        </td>
                        <td style={S.td}>
                          <input style={S.cellInput} value={val('notes')}
                            onChange={e => set('notes', e.target.value)} placeholder="Optional" />
                        </td>
                      </tr>
                    );
                  }
                  /* PI form */
                  return (
                    <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'transparent' : 'rgba(45,55,72,0.3)' }}>
                      <td style={S.td}>{row.procedure}</td>
                      <td style={S.td}>
                        <select style={S.cellInput} value={val('finding') || 'Normal'}
                          onChange={e => set('finding', e.target.value)}>
                          {['Normal', 'Abnormal', 'Not Done'].map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input style={S.cellInput} value={val('details')}
                          onChange={e => set('details', e.target.value)} placeholder="Details" />
                      </td>
                      <td style={S.td}>
                        <input style={S.cellInput} value={val('signed_by')}
                          onChange={e => set('signed_by', e.target.value)} placeholder="PI Name" />
                      </td>
                      <td style={S.td}>
                        <input style={{ ...S.cellInput, minWidth: 170 }} type="datetime-local"
                          value={val('timestamp') || nowISO()}
                          onChange={e => set('timestamp', e.target.value)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Submit */}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <button style={{ ...btnStyle('#48bb78'), opacity: submitting ? 0.6 : 1 }}
              onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Form'}
            </button>
          </div>
        </div>
      )}

      {/* Submissions table */}
      {submissions.length > 0 && (
        <div style={S.card}>
          <h3 style={S.subheading}>Recent Submissions</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Form ID', 'Patient', 'Visit', 'Type', 'Submitted By', 'Date', 'Status'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {submissions.map((s, i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(45,55,72,0.3)' }}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{s.form_id}</td>
                    <td style={S.td}>{s.patient_id}</td>
                    <td style={S.td}>{s.visit_name}</td>
                    <td style={S.td}>
                      <span style={{
                        backgroundColor: s.form_type === 'nurse' ? 'rgba(66,153,225,0.15)' : 'rgba(236,201,75,0.15)',
                        color: s.form_type === 'nurse' ? '#63b3ed' : '#ecc94b',
                        padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      }}>
                        {s.form_type === 'nurse' ? 'Nurse' : 'PI'}
                      </span>
                    </td>
                    <td style={S.td}>{s.submitted_by}</td>
                    <td style={{ ...S.td, color: '#a0aec0', fontSize: 13 }}>
                      {new Date(s.timestamp).toLocaleString()}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        backgroundColor: 'rgba(72,187,120,0.15)', color: '#48bb78',
                        padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      }}>
                        Submitted
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helper: group fields by procedure ── */
interface ProcedureRow {
  procedure: string;
  fields: FormField[];
  metadata: Record<string, string>;
}

function groupFieldsByProcedure(fields: FormField[]): ProcedureRow[] {
  const map = new Map<string, ProcedureRow>();
  for (const f of fields) {
    if (!map.has(f.procedure)) {
      map.set(f.procedure, { procedure: f.procedure, fields: [], metadata: {} });
    }
    const row = map.get(f.procedure)!;
    row.fields.push(f);
    if (f.metadata) Object.assign(row.metadata, f.metadata);
  }
  return Array.from(map.values());
}
